import { durationAcrossMidnight } from './crossMidnight'
import { computeNightMinutes, type NightConfig } from './nightHours'

export type TimeSlot = {
  readonly start: string
  readonly end: string
}

export type SlotInput = TimeSlot | null | undefined

export type SlotTotals = {
  readonly totalMinutes: number
  readonly dayMinutes: number
  readonly nightMinutes: number
}

/**
 * Aggregates a list of time slots into total, day and night minutes.
 *
 * - Each slot delegates to `durationAcrossMidnight` for total minutes and
 *   `computeNightMinutes` for the night portion, under the given `config`
 *   (defaults to `DEFAULT_NIGHT_CONFIG` via `computeNightMinutes`).
 * - `null` / `undefined` entries are silently ignored so callers can pass
 *   sparse driver inputs without pre-filtering.
 * - Slots are summed as-is: no merge, no deduplication, no overlap detection.
 *   Two overlapping slots count their shared interval twice — this is the
 *   explicit business intent (drivers are paid per declared slot).
 * - `dayMinutes` is derived as `totalMinutes - nightMinutes`, which guarantees
 *   the invariant `totalMinutes === dayMinutes + nightMinutes` by construction.
 *
 * Throws `InvalidNightConfigError` when `config` is out of range, via
 * `computeNightMinutes`.
 *
 * Why: quarter-hour billing aggregates a driver's day from a raw list of
 * slots in a pure, deterministic way — no `Date`, no I/O, no hidden state.
 */
export function calculateSlots(
  slots: readonly SlotInput[],
  config?: NightConfig,
): SlotTotals {
  let totalMinutes = 0
  let nightMinutes = 0

  for (const slot of slots) {
    if (slot === null || slot === undefined) {
      continue
    }

    totalMinutes += durationAcrossMidnight(slot.start, slot.end)
    nightMinutes += computeNightMinutes(slot.start, slot.end, config)
  }

  return {
    totalMinutes,
    dayMinutes: totalMinutes - nightMinutes,
    nightMinutes,
  }
}
